/**
 * Where the colour mode applies, and the provider that enforces it.
 *
 * The public website has no colour mode. Only jaen's own pages (the CMS tree,
 * login, logout, settings, signup) and the app plugin's pages under /app get
 * one, with the site's `colorMode.default` as the mode a visitor lands on and
 * the toggle on top. Everywhere else next-themes is told `forcedTheme="light"`,
 * which writes the light class onto <html> whatever is stored, persists
 * nothing, and lifts again the moment the route changes into an area that has
 * a mode. The stored choice a visitor made inside the app therefore survives a
 * detour over the public pages.
 *
 * One provider, not two: next-themes owns the class on <html>, and two
 * providers would fight over it. The route decides the `forcedTheme` prop and
 * nothing else.
 *
 * The prefixes are the routes, not the page components: jaen's system routes
 * are never localized (gatsby-source-jaen leaves them unprefixed), and the app
 * plugin registers its pages under /app the same way, so a plain prefix match
 * against the pathname is exact. The no-flash script in gatsby-ssr.tsx runs
 * the same match before the first paint, from the same list.
 */
import {globalHistory} from '@reach/router'
import {ThemeProvider as NextThemeProvider} from 'next-themes'
import {useSyncExternalStore, type FC, type ReactNode} from 'react'

export type ColorModeDefault = 'light' | 'dark' | 'system'

/**
 * The localStorage key the visitor's own choice lives under, written by the
 * toggle through next-themes and read by the no-flash script in
 * gatsby-ssr.tsx before the first paint. The two must agree, which is why it
 * is exported from here and repeated nowhere.
 *
 * It is jaen's own key on purpose. next-themes' default `theme` and Chakra
 * v2's `chakra-ui-color-mode` are never read: v2 wrote `light` for every
 * visitor it ever painted, and adopting either would hand every returning
 * desktop browser a light panel on a site whose default is dark. A visitor
 * who chose light on purpose chooses it once more, and from then on the
 * choice is kept here.
 */
export const COLOR_MODE_STORAGE_KEY = 'jaen:colorMode'

/** Route prefixes that have a colour mode. Everything else is forced light. */
export const COLOR_MODE_ROUTE_PREFIXES = [
  '/cms',
  '/login',
  '/logout',
  '/settings',
  '/signup',
  /*
    The OIDC callback is not a jaen page but every site ships one at this
    path, and it sits between the login and the app. Forcing it light would
    flash a white screen into a dark app on the way to the dashboard.
  */
  '/loading',
  '/app',
  /*
    gatsby-plugin-offline's app shell. The service worker serves this one
    page for every navigation whose resources it holds, online and offline
    alike, so its markup stands in for /app/dashboard and every other route
    of the app on a phone that runs the installed PWA. Left out of the list
    it was built with forcedTheme light, and the offline app opened light on
    a dark brand. The route a visitor is really on is decided at run time
    from window.location, by the no-flash script and by useScopedPathname,
    so listing the shell only frees its server render from the forced light.
  */
  '/offline-plugin-app-shell-fallback'
] as const

export const hasColorMode = (pathname: string): boolean => {
  const path = pathname.replace(/\/+$/, '') || '/'

  return COLOR_MODE_ROUTE_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`)
  )
}

const subscribe = (onChange: () => void) => globalHistory.listen(onChange)

const getSnapshot = () => globalHistory.location.pathname

interface ColorModeScopeProps {
  /**
   * The page path build-html hands wrapRootElement. The browser bootstrap
   * gets none, and there window.location says the same thing the server
   * knew, so the hydration render agrees with the markup.
   */
  ssrPathname?: string
  defaultMode: ColorModeDefault
  children: ReactNode
}

/**
 * The current pathname, live across client-side navigation, and on the
 * server the one build-html handed in. The one place the route is read for
 * the decisions that follow the route list above, so ColorModeScope and the
 * consent banner's scope in wrap-root-element.tsx agree on every page.
 */
export const useScopedPathname = (ssrPathname?: string): string =>
  useSyncExternalStore(
    subscribe,
    getSnapshot,
    () =>
      ssrPathname ??
      (typeof window !== 'undefined' ? window.location.pathname : '/')
  )

/**
 * next-themes' own inline script is rendered inert. It bakes the page's
 * forcedTheme and default into the HTML and knows nothing of the route, and
 * one page, the offline app shell above, is served for every route the
 * service worker holds: with that script live the shell painted the site
 * default onto the public pages and, before the shell was listed, forced
 * light onto the app. The no-flash script in gatsby-ssr.tsx already makes
 * the same decision before the first paint, from this list and the same
 * storage key, against the URL the visitor is really on, so it is the one
 * script that paints. next-themes applies the theme again in its mount
 * effect, from the same key, and takes over from there. There is no prop to
 * drop the script, hence the type the browser does not execute.
 */
const INERT_SCRIPT = {type: 'text/plain'} as const

export const ColorModeScope: FC<ColorModeScopeProps> = ({
  ssrPathname,
  defaultMode,
  children
}) => {
  const pathname = useScopedPathname(ssrPathname)

  return (
    <NextThemeProvider
      attribute="class"
      defaultTheme={defaultMode}
      forcedTheme={hasColorMode(pathname) ? undefined : 'light'}
      storageKey={COLOR_MODE_STORAGE_KEY}
      scriptProps={INERT_SCRIPT}
      enableSystem
      disableTransitionOnChange>
      {children}
    </NextThemeProvider>
  )
}
